import { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Settings as SettingsIcon, BookOpen,
  LogOut, Eye, EyeOff, Layers, Image as ImageIcon, Users, Sparkles, ChevronRight, PanelRightOpen, PanelRightClose,
  Compass, MessageSquare, Mail, Inbox, DatabaseBackup
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { resolveUrl } from "./api";
import { PreviewProvider, usePreview } from "./PreviewContext";
import { PreviewPane } from "./components/PreviewPane";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { type: "section", label: "Homepage Sections" },
  { to: "/admin/content/hero", label: "Hero", icon: Sparkles },
  { to: "/admin/content/stats", label: "Stats Bar", icon: Layers },
  { to: "/admin/content/philosophy", label: "Philosophy", icon: FileText },
  { to: "/admin/content/coach", label: "Coach Mike", icon: Users },
  { to: "/admin/content/book", label: "Book", icon: BookOpen },
  { to: "/admin/content/ticker", label: "Ticker Words", icon: ChevronRight },
  { to: "/admin/content/logo_strip", label: "Accreditation Strip", icon: Layers },
  { to: "/admin/content/programs_header", label: "Programs Header", icon: FileText },
  { to: "/admin/content/newsletter", label: "Newsletter", icon: FileText },
  { to: "/admin/content/footer", label: "Footer", icon: FileText },
  { type: "section", label: "Pages" },
  { to: "/admin/content/page_about", label: "About / What is BDH", icon: Compass },
  { to: "/admin/content/page_philosophy", label: "Philosophy Page", icon: Compass },
  { to: "/admin/content/page_programs", label: "Programs Page", icon: Layers },
  { to: "/admin/content/page_coach", label: "Coach Page", icon: Users },
  { to: "/admin/content/page_book", label: "Book Page", icon: BookOpen },
  { to: "/admin/content/page_contact", label: "Contact Page", icon: Mail },
  { to: "/admin/content/page_privacy", label: "Privacy Policy", icon: FileText },
  { to: "/admin/content/page_terms", label: "Terms of Use", icon: FileText },
  { to: "/admin/content/page_cookies", label: "Cookie Policy", icon: FileText },
  { type: "section", label: "Manage" },
  { to: "/admin/programs", label: "Programs", icon: Layers },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/admin/posts", label: "Posts & Custom Types", icon: FileText },
  { type: "section", label: "Site" },
  { to: "/admin/settings", label: "Site Settings", icon: SettingsIcon },
  { to: "/admin/snapshot", label: "Content Snapshots", icon: DatabaseBackup },
];

export const AdminLayout = () => (
  <PreviewProvider>
    <AdminLayoutInner />
  </PreviewProvider>
);

const AdminLayoutInner = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logo, setLogo] = useState(null);
  const { open: previewOpen, toggle: togglePreview } = usePreview();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/settings`)
      .then((r) => r.json())
      .then((d) => setLogo(d?.data?.logo_url));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-body">
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b border-white/10 bg-[#0a0a0a]/95 backdrop-blur">
        <Link to="/admin" className="flex items-center gap-2">
          {logo && <img src={resolveUrl(logo)} alt="" className="h-7 w-7 object-contain" />}
          <span className="font-display uppercase tracking-tight text-base">BDH Admin</span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={togglePreview}
            className={`px-2 py-1.5 text-[10px] tracking-[0.2em] uppercase font-bold transition-colors ${
              previewOpen ? "bg-brand text-white" : "text-zinc-400 hover:text-white border border-white/15"
            }`}
            data-testid="admin-preview-toggle-mobile"
          >
            {previewOpen ? "Hide Preview" : "Preview"}
          </button>
          <button onClick={() => setMobileOpen((v) => !v)} className="text-zinc-400 text-xs uppercase tracking-widest" data-testid="admin-mobile-toggle">
            {mobileOpen ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      <div
        className={`lg:grid min-h-screen transition-[grid-template-columns] duration-300 ${
          previewOpen ? "lg:grid-cols-[230px_1fr_42vw]" : "lg:grid-cols-[260px_1fr]"
        }`}
      >
        {/* Sidebar */}
        <aside
          className={`${mobileOpen ? "block" : "hidden"} lg:block border-r border-white/10 bg-[#0c0c0c] sticky top-0 h-auto lg:h-screen lg:overflow-y-auto`}
        >
          <div className="hidden lg:flex items-center gap-3 px-5 h-16 border-b border-white/10">
            {logo && <img src={resolveUrl(logo)} alt="" className="h-8 w-8 object-contain" />}
            <div className="leading-none">
              <div className="font-display uppercase text-base tracking-tight">BDH Admin</div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold mt-1">Control Panel</div>
            </div>
          </div>
          <nav className="px-3 py-4 space-y-1">
            {NAV.map((n, i) => {
              if (n.type === "section") {
                return (
                  <div key={`s-${i}`} className="px-3 pt-5 pb-2 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-bold">
                    {n.label}
                  </div>
                );
              }
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                      isActive
                        ? "bg-brand text-white"
                        : "text-zinc-400 hover:text-white hover:bg-white/5"
                    }`
                  }
                  data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{n.label}</span>
                </NavLink>
              );
            })}
          </nav>

          <div className="px-3 pt-2 pb-6 border-t border-white/10 mt-4 space-y-2">
            <button
              onClick={togglePreview}
              className={`hidden lg:flex w-full items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                previewOpen ? "bg-brand text-white" : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid="admin-preview-toggle"
            >
              {previewOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
              {previewOpen ? "Hide Live Preview" : "Show Live Preview"}
            </button>
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-400 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
              data-testid="admin-view-site"
            >
              <Eye className="h-4 w-4" /> View Site
            </a>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-400 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
            <div className="px-3 pt-3 text-[10px] text-zinc-600">
              {user?.email}
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="px-4 sm:px-8 lg:px-10 py-8 lg:py-12 w-full max-w-full lg:max-w-none min-w-0">
          <Outlet />
        </main>

        {/* Live Preview Pane */}
        <PreviewPane />
      </div>
    </div>
  );
};
